import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../utils/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Trigger button label for a11y; falls back to current selection/placeholder */
  ariaLabel?: string;
}

interface PanelPos {
  top: number;
  left: number;
  width: number;
  triggerCenter: number;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  error,
  className,
  ariaLabel,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [pos, setPos] = useState<PanelPos | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const selected = options.find((o) => o.value === value);
  const selectedIndex = selected ? options.indexOf(selected) : -1;

  // Compute panel position from trigger rect (so it floats outside any
  // overflow-hidden ancestor and matches trigger width exactly)
  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.bottom + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
      triggerCenter: r.left + r.width / 2 + window.scrollX,
    });
  }, []);

  // Open: measure + reposition on scroll/resize while open
  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
    const onScroll = () => updatePos();
    const onResize = () => updatePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePos]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        listRef.current &&
        !listRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reset active index when opening
  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [open, selectedIndex]);

  // Scroll active option into view
  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const commit = useCallback(
    (idx: number) => {
      const opt = options[idx];
      if (!opt || opt.disabled) return;
      onChange?.(opt.value);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [options, onChange]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setActiveIndex((i) => {
          const next = i < 0 ? 0 : Math.min(options.length - 1, i + 1);
          return options[next]?.disabled ? Math.min(options.length - 1, next + 1) : next;
        });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setActiveIndex((i) => {
          const next = i < 0 ? options.length - 1 : Math.max(0, i - 1);
          return options[next]?.disabled ? Math.max(0, next - 1) : next;
        });
        break;
      }
      case 'Home': {
        e.preventDefault();
        setActiveIndex(0);
        break;
      }
      case 'End': {
        e.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        commit(activeIndex);
        break;
      }
      case 'Escape':
      case 'Tab': {
        setOpen(false);
        break;
      }
    }
  };

  // SSR safety: portal only after mount
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={cn(
          'w-full px-4 py-2.5 pr-10 border rounded-lg text-left select-none',
          'transition-all duration-200',
          // base
          'bg-surface text-slate-100 cursor-pointer',
          open
            ? 'border-primary shadow-[0_0_0_1px_#3B82F6]'
            : 'border-slate-700 hover:border-slate-600 focus:outline-none focus:border-primary focus:shadow-[0_0_0_1px_#3B82F6]',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-red-500 shadow-[0_0_0_1px_#EF4444]',
          className
        )}
      >
        <span
          className={cn('block truncate text-sm leading-tight', !selected && 'text-slate-500')}
          style={{
            // Reserve enough right padding so the chevron (16px wide,
            // positioned 12px from the right) never overlaps the text.
            paddingRight: '2.25rem',
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            width: 16,
            height: 16,
            pointerEvents: 'none',
            transform: open ? 'translate(0, -50%) rotate(180deg)' : 'translate(0, -50%)',
            transition: 'transform 200ms, color 200ms',
            color: open ? '#3B82F6' : '#94A3B8',
          }}
        />
      </button>

      {open && mounted && pos &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: pos.top,
              left: pos.left,
              // Panel width: at least 280px so long labels don't get squeezed
              // or wrap. Anchored to the trigger's left edge.
              width: Math.max(pos.width, 280),
              animation: 'selectPanelSlideUp 0.2s ease-out',
              zIndex: 1000,
            }}
          >
            {/* Caret pointing up to the trigger */}
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: -5,
                left: Math.max(12, Math.min((Math.max(pos.width, 280)) - 12, pos.triggerCenter - pos.left - 5)),
                width: 10,
                height: 10,
                backgroundColor: '#0F172A',
                borderLeft: '1px solid #475569',
                borderTop: '1px solid #475569',
                transform: 'rotate(45deg)',
              }}
            />
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              tabIndex={-1}
              aria-activedescendant={
                activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
              }
              style={{
                marginTop: 6,
                maxHeight: '15rem',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                // Opaque, deep background — no transparency so cards behind
                // never bleed through.
                backgroundColor: '#0F172A',
                border: '1px solid #475569',
                borderRadius: '0.5rem',
                // Heavier shadow + ring for clear separation from page
                boxShadow:
                  '0 0 0 1px rgba(15, 23, 42, 0.9), 0 25px 50px -12px rgba(0, 0, 0, 0.75), 0 8px 16px -4px rgba(0, 0, 0, 0.4)',
                padding: '0.375rem',
                listStyle: 'none',
                margin: 0,
              }}
            >
              {options.length === 0 && (
                <li style={{ padding: '0.75rem 1.25rem', fontSize: '0.875rem', color: '#64748B' }}>No options</li>
              )}
              {options.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isActive = idx === activeIndex;
                const baseLiStyle: React.CSSProperties = {
                  position: 'relative',
                  cursor: opt.disabled ? 'not-allowed' : 'pointer',
                  userSelect: 'none',
                  paddingTop: '0.625rem',
                  paddingBottom: '0.625rem',
                  // More generous left/right padding — text no longer hugs
                  // the edge, and the selected-state bar sits comfortably
                  // inside the row.
                  paddingLeft: '1.5rem',
                  paddingRight: '1.25rem',
                  marginLeft: '0.25rem',
                  marginRight: '0.25rem',
                  marginTop: '0.125rem',
                  marginBottom: '0.125rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  transition: 'background-color 150ms, color 150ms',
                  opacity: opt.disabled ? 0.4 : 1,
                  color: isSelected ? '#3B82F6' : '#E2E8F0',
                  fontWeight: isSelected ? 500 : 400,
                  backgroundColor: isSelected
                    ? 'rgba(59, 130, 246, 0.12)'
                    : isActive
                    ? 'rgba(51, 65, 85, 0.7)'
                    : 'transparent',
                };
                return (
                  <li
                    key={opt.value}
                    id={`${listboxId}-opt-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled}
                    onMouseEnter={() => !opt.disabled && setActiveIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => !opt.disabled && commit(idx)}
                    style={baseLiStyle}
                  >
                    {isSelected && (
                      <span
                        aria-hidden
                        style={{
                          position: 'absolute',
                          left: 6,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: 18,
                          borderRadius: '9999px',
                          backgroundColor: '#3B82F6',
                        }}
                      />
                    )}
                    <span
                      style={{
                        flex: 1,
                        // Hard single-line — never wrap long labels
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                      }}
                    >
                      {opt.label}
                    </span>
                    {isSelected && (
                      <Check
                        style={{ width: 16, height: 16, flexShrink: 0, color: '#3B82F6', strokeWidth: 2.5 }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )}

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}
